import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useCallback } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { useTranslation } from "react-i18next";
import { PageContainer } from "@/components/ui/page-container";
import { MobilePageTitle } from "@/components/ui/mobile-page-title";
import { Skeleton } from "@/components/ui/skeleton";
import { ResourceError } from "@/components/ui/resource-error";
import { Wizard } from "@/components/ui/wizard";
import { ConfirmDialog } from "@/components/ui/dialog/confirm-dialog";
import { toast } from "@/components/ui/toast";
import { useUnsavedChanges } from "@/hooks/use-unsaved-changes";
import { useAutoCollapsedSidebar } from "@/hooks/use-auto-collapsed-sidebar";
import { useGymChangeHandler } from "@/hooks/use-gym-change-handler";
import { useAthleteNameBreadcrumb } from "@/hooks/use-breadcrumb/use-breadcrumb-overrides";
import { PROTECTED_ROUTES } from "@/utils/navigation";
import { getApiErrorMessages } from "@/utils/api-error";
import {
  AthleteCreationSchema,
  type AthleteCreationFormData,
} from "@/schemas/athlete";
import { useAthleteWizardSteps } from "@/components/create-athlete/use-athlete-wizard-steps";
import { athleteFormToUpdatePayload } from "@/components/create-athlete/athlete-payload";
import { athleteToFormValues } from "@/components/create-athlete/athlete-to-form-values";
import { mainAddressOf } from "@/utils/main-address";
import { useAthlete, useUpdateAthlete } from "@/queries/use-athletes";
import type { Athlete } from "@/api/athletes/types";

export const Route = createFileRoute("/(protected)/athletes/$athleteId/edit/")({
  component: RouteComponent,
});

function RouteComponent() {
  useAutoCollapsedSidebar();
  const { t } = useTranslation();
  const { athleteId } = Route.useParams();
  const { data: athlete, isLoading, error } = useAthlete(Number(athleteId));

  useAthleteNameBreadcrumb(Number(athleteId));

  return (
    <div className="flex h-full flex-col px-6 py-5">
      <MobilePageTitle>{t("pages:athletes.wizard.editTitle")}</MobilePageTitle>
      <PageContainer
        className="flex min-h-0 min-w-[309px] flex-1 flex-col p-0 md:m-0 lg:mb-0"
        internalScroll
      >
        {error ? (
          <ResourceError
            error={error}
            notFoundMessage={t("pages:profile.basicData.errors.notFound")}
            errorMessage={t("pages:profile.basicData.errors.loadingFailed", {
              message: error.message,
            })}
            className="m-6"
          />
        ) : isLoading || !athlete ? (
          <div className="flex flex-col gap-4 p-8">
            <Skeleton className="h-8 w-64" />
            <Skeleton className="h-40 w-full" />
            <Skeleton className="h-40 w-full" />
          </div>
        ) : (
          <EditAthleteWizard athlete={athlete} />
        )}
      </PageContainer>
    </div>
  );
}

// Mounted once the athlete has loaded, so the form starts from their values
// and a background refetch can't reset what's being edited.
function EditAthleteWizard({ athlete }: { athlete: Athlete }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { mutate: updateAthlete, isPending } = useUpdateAthlete();
  const steps = useAthleteWizardSteps({
    currentAvatarUrl: athlete.avatarUrl,
    emailDisabled: true,
  });

  const form = useForm<AthleteCreationFormData>({
    resolver: zodResolver(AthleteCreationSchema()),
    mode: "onSubmit",
    defaultValues: athleteToFormValues(athlete),
  });

  const navigateBack = useCallback(
    () =>
      navigate({
        to: PROTECTED_ROUTES.ATHLETE_DETAIL,
        params: { athleteId: String(athlete.id) },
        search: { tab: "basic-data" },
      }),
    [navigate, athlete.id],
  );

  const { handleCancelEdit, unsavedChangesDialogProps } = useUnsavedChanges({
    isEditing: true,
    isDirty: form.formState.isDirty,
  });

  useGymChangeHandler({
    hasUnsavedChanges: form.formState.isDirty,
    handleCancelEdit,
    navigateOnChange: PROTECTED_ROUTES.ATHLETES,
  });

  const handleComplete = (data: AthleteCreationFormData) => {
    updateAthlete(
      {
        id: athlete.id,
        data: athleteFormToUpdatePayload(
          data,
          mainAddressOf(athlete.addresses)?.id,
        ),
      },
      {
        onSuccess: () => {
          toast.success(
            t("pages:profile.basicData.toaster.update.success.title"),
            {
              description: t(
                "pages:profile.basicData.toaster.update.success.description",
              ),
            },
          );
          navigateBack();
        },
        onError: (error) => {
          toast.error(t("pages:profile.basicData.toaster.update.error.title"), {
            description: getApiErrorMessages(error),
          });
        },
      },
    );
  };

  return (
    <>
      <Wizard
        form={form}
        steps={steps}
        onComplete={handleComplete}
        isSubmitting={isPending}
        onCancel={() => handleCancelEdit(navigateBack)}
      />
      <ConfirmDialog {...unsavedChangesDialogProps} />
    </>
  );
}
